import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiFetch } from '../../../../apiFetch';
import './ApplyVSNIndexForm.css';

const ApplyVSNIndexForm = () => {
  const { userId: playerId } = useParams();
  const navigate = useNavigate();
  const redirectTimeoutRef = useRef(null);

  const FRONTEND_URL =
    process.env.REACT_APP_FRONTEND_URL || window.location.origin;

  const [profileSlug, setProfileSlug] = useState(
    localStorage.getItem('playerProfileSlug') || ''
  );

  const [playerData, setPlayerData] = useState(null);

  // ✅ Email is derived from playerData (view-only) — no local editable state needed
  const [leagueName, setLeagueName] = useState('');
  const [clubWebsite, setClubWebsite] = useState('');
  const [introduction, setIntroduction] = useState('');

  // ✅ YouTube is now derived from playerData (view-only) — no local editable state needed
  const [youtubeLink, setYoutubeLink] = useState('');

  const [photo, setPhoto] = useState(null);
  const [idDocument, setIdDocument] = useState(null);
  const [leagueIdCard, setLeagueIdCard] = useState(null);
  const [agentDetail, setAgentDetail] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const [clubStats, setClubStats] = useState([
    {
      club: '',
      from: '',
      to: '',
      leagueName: '',
      leagueTier: '',
      positionPlayed: '',
      matches: '',
      goals: '',
      assists: '',
      cleanSheets: '',
      clubWebsite: '',
      clubPhoto: null,
    },
  ]);

  // ✅ Cleanup redirect timer on unmount
  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const fetchPlayer = async () => {
      try {
        const res = await apiFetch(`/api/player-details/${playerId}`);
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();

        setPlayerData(data);

        // ✅ Prefill YouTube highlight field from DB (youtube_channel_id)
        const ytRaw = data?.youtube_channel_id || '';
        const ytValue =
          ytRaw && !ytRaw.startsWith('http')
            ? `https://www.youtube.com/channel/${ytRaw}`
            : ytRaw;

        setYoutubeLink(ytValue);

        // ✅ Prefer API slug if available
        if (data?.profileSlug) {
          setProfileSlug(data.profileSlug);
        }
      } catch (err) {
        console.error('Error fetching player:', err);
      }
    };

    if (playerId) fetchPlayer();
  }, [playerId]);

  const updateClubStats = (index, field, value) => {
    const newStats = [...clubStats];
    newStats[index][field] = value;
    setClubStats(newStats);
  };

  const updateClubPhoto = (index, file) => {
    const newStats = [...clubStats];
    newStats[index].clubPhoto = file;
    setClubStats(newStats);
  };

  const addClubRow = () => {
    setClubStats([
      ...clubStats,
      {
        club: '',
        from: '',
        to: '',
        leagueName: '',
        leagueTier: '',
        positionPlayed: '',
        matches: '',
        goals: '',
        assists: '',
        cleanSheets: '',
        clubWebsite: '',
        clubPhoto: null,
      },
    ]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!FRONTEND_URL) {
      alert('Config error: REACT_APP_FRONTEND_URL is missing.');
      return;
    }

    if (!playerData?.id) {
      alert('Player data not loaded yet. Please wait.');
      return;
    }

    // ✅ Email is authoritative and view-only
    const playerEmail = playerData?.loginEmail || playerData?.emailAddress || '';

    if (!playerEmail) {
      alert('Your account email is missing. Please contact support.');
      return;
    }

    const effectiveSlug = profileSlug || playerData?.profileSlug;
    if (!effectiveSlug) {
      alert('Profile slug missing. Please log in again.');
      return;
    }

    const profileLink = `${FRONTEND_URL}/player-profile/${effectiveSlug}`;

    let clubStatsFormatted = 'Player Club History & Stats:\n\n';
    clubStats.forEach((c, i) => {
      clubStatsFormatted += `#${i + 1}: ${c.club} (${c.from}–${c.to})\n`;
      clubStatsFormatted += `- League: ${c.leagueName} (${c.leagueTier})\n`;
      clubStatsFormatted += `- Position Played: ${c.positionPlayed || 'N/A'}\n`;
      clubStatsFormatted += `- Matches: ${c.matches || 0}, Goals: ${c.goals || 0}, Assists: ${c.assists || 0}, Clean Sheets: ${c.cleanSheets || 0}\n`;
      clubStatsFormatted += `- Club Website: ${c.clubWebsite || 'N/A'}\n\n`;
    });

    // ✅ YouTube link is view-only; use the current state (prefilled from DB)
    const ytToSend = youtubeLink || '';

    const emailBody = `
Dear VSN Index Team,

My name is Volto, and I’m reaching out on behalf of Virtual Scouting Network (VSN) — a global scouting platform — to request the creation of a VSN Index profile for one of our members.

We are submitting the following player’s information for your review, with the hope that you may consider creating or updating their profile on VSN Index. All details have been verified through our internal review process.

Once the profile has been created, we kindly ask that you forward the confirmation or profile link to virtualscoutingnetwork@gmail.com and also to the player’s personal email for their records.

The player’s details are included below:

- Full Name: ${playerData?.fullName}
- Email: ${playerEmail}
- Date of Birth: ${playerData?.dob}
- Nationality: ${playerData?.country}
- Primary Position: ${playerData?.primaryPosition}
- Secondary Position: ${playerData?.secondaryPosition}
- Height: ${playerData?.height} m
- Weight: ${playerData?.weight} kg
- Preferred Foot: ${playerData?.preferredFoot}
- Current Club: ${playerData?.currentClub}
- Club Country: ${playerData?.clubCountry}
- Previous Club: ${playerData?.previousClub}
- League Name: ${leagueName}
- Club Website: ${clubWebsite}
- Agent Detail: ${agentDetail || 'N/A'}
- Introduction: ${introduction}
- YouTube Link: ${ytToSend}
- VSN Profile Link: ${profileLink}

${clubStatsFormatted}

Warm regards,  
Volto  
Virtual Scouting Network
    `;

    const formData = new FormData();

    formData.append('playerId', playerData.id);
    formData.append('emailBody', emailBody);
    formData.append('leagueName', leagueName);
    formData.append('clubWebsite', clubWebsite);
    formData.append('introduction', introduction);

    // ✅ keep sending youtubeLink exactly as before (no progress lost)
    formData.append('youtubeLink', ytToSend);

    // ✅ Send authoritative email (not user-edited)
    formData.append('emailAddress', playerEmail);

    formData.append('agentDetail', agentDetail);

    if (photo) formData.append('photo', photo);
    if (idDocument) formData.append('governmentId', idDocument);
    if (leagueIdCard) formData.append('leagueIdCard', leagueIdCard);

    clubStats.forEach((club, index) => {
      formData.append(`club_${index}`, club.club);
      formData.append(`from_${index}`, club.from);
      formData.append(`to_${index}`, club.to);
      formData.append(`leagueName_${index}`, club.leagueName);
      formData.append(`leagueTier_${index}`, club.leagueTier);
      formData.append(`positionPlayed_${index}`, club.positionPlayed);
      formData.append(`matches_${index}`, club.matches || 0);
      formData.append(`goals_${index}`, club.goals || 0);
      formData.append(`assists_${index}`, club.assists || 0);
      formData.append(`cleanSheets_${index}`, club.cleanSheets || 0);
      formData.append(`clubWebsite_${index}`, club.clubWebsite || '');

      if (club.clubPhoto) {
        formData.append(`clubPhoto_${index}`, club.clubPhoto);
      }
    });

    try {
      const res = await apiFetch(`/api/apply-transfermarkt`, {
        method: 'POST',
        body: formData,
      });

      if (res.ok) {
        setSubmitted(true);

        redirectTimeoutRef.current = setTimeout(() => {
          const slug = profileSlug || playerData?.profileSlug;
          if (slug) {
            navigate(`/player-profile/${slug}`, { replace: true });
          } else {
            navigate('/player-dashboard', { replace: true });
          }
        }, 1500);
      } else {
        const errorText = await res.text();
        console.error('Application failed:', errorText);
      }
    } catch (err) {
      console.error('Error submitting application:', err);
    }
  };

  if (submitted) {
    return (
      <div className="success-message">
        ✅ Your VSN Index application has been submitted successfully.
      </div>
    );
  }

  if (!playerData) return <div>Loading...</div>;

  const playerEmail = playerData?.loginEmail || playerData?.emailAddress || '';

  return (
    <div className="transfermarkt-form-container">
      <h2>Apply for VSN Index via VSN</h2>

      <form
        onSubmit={handleSubmit}
        className="transfermarkt-form"
        encType="multipart/form-data"
      >
        <label>Full Name</label>
        <input type="text" value={playerData.fullName} disabled />

        <label>Email Address</label>
        <input type="email" value={playerEmail} readOnly disabled />

        <label>Date of Birth</label>
        <input type="text" value={playerData.dob} disabled />

        <label>Nationality</label>
        <input type="text" value={playerData.country} disabled />

        <label>Primary & Secondary Position</label>
        <input
          type="text"
          value={`${playerData.primaryPosition || ''}, ${playerData.secondaryPosition || ''}`}
          disabled
        />

        <label>Height & Weight</label>
        <input
          type="text"
          value={`${playerData.height || '-'} m / ${playerData.weight || '-'} kg`}
          disabled
        />

        <label>Preferred Foot</label>
        <input type="text" value={playerData.preferredFoot || ''} disabled />

        <label>Current Club & Country</label>
        <input
          type="text"
          value={`${playerData.currentClub || ''}, ${playerData.clubCountry || ''}`}
          disabled
        />

        <label>Previous Club</label>
        <input type="text" value={playerData.previousClub || ''} disabled />

        <label>VSN Profile Link</label>
        <input
          type="text"
          value={
            profileSlug || playerData?.profileSlug
              ? `${FRONTEND_URL}/player-profile/${profileSlug || playerData?.profileSlug}`
              : 'Loading profile link...'
          }
          disabled
        />

        <label>League Name</label>
        <input
          type="text"
          value={leagueName}
          onChange={(e) => setLeagueName(e.target.value)}
          required
        />

        <label>Club Website (if available)</label>
        <input
          type="text"
          value={clubWebsite}
          onChange={(e) => setClubWebsite(e.target.value)}
        />

        <label>Agent Detail (Optional)</label>
        <input
          type="text"
          placeholder="Agent Name, Agency Name"
          value={agentDetail}
          onChange={(e) => setAgentDetail(e.target.value)}
        />

        <label>Introduce Yourself (e.g., background, club history, position)</label>
        <textarea
          value={introduction}
          onChange={(e) => setIntroduction(e.target.value)}
          required
        />

        {/* ✅ Removed "(optional)" and made read-only without changing the label text */}
        <label>YouTube Highlight Video</label>
        <input
          type="text"
          value={youtubeLink}
          readOnly
          disabled
          placeholder="https://youtube.com/your-video"
        />

        <label>Upload a Passport Photo of you in your Local Club Jersey</label>
        <input
          type="file"
          accept="image/*"
          onChange={(e) => setPhoto(e.target.files[0])}
        />

        <label>Upload Government I.D or Passport</label>
        <input
          type="file"
          accept="image/*,application/pdf"
          onChange={(e) => setIdDocument(e.target.files[0])}
          required
        />

        <label>Upload League I.D Card or League Supporting Documents</label>
        <input
          type="file"
          accept="image/*,application/pdf"
          onChange={(e) => setLeagueIdCard(e.target.files[0])}
          required
        />

        <h3>Club History & Official Stats</h3>
        {clubStats.map((entry, index) => (
          <div key={index} className="club-history-row">
            <input
              type="text"
              placeholder="Club Name"
              value={entry.club}
              onChange={(e) => updateClubStats(index, 'club', e.target.value)}
              required
            />
            <input
              type="text"
              placeholder="From Year"
              value={entry.from}
              onChange={(e) => updateClubStats(index, 'from', e.target.value)}
              required
            />
            <input
              type="text"
              placeholder="To Year"
              value={entry.to}
              onChange={(e) => updateClubStats(index, 'to', e.target.value)}
              required
            />
            <input
              type="text"
              placeholder="League Name"
              value={entry.leagueName}
              onChange={(e) => updateClubStats(index, 'leagueName', e.target.value)}
              required
            />
            <select
              value={entry.leagueTier}
              onChange={(e) => updateClubStats(index, 'leagueTier', e.target.value)}
              required
            >
              <option value="">Select League Tier</option>
              <option value="Top Tier">Top Tier</option>
              <option value="Second Division">Second Division</option>
              <option value="Academy">Academy</option>
              <option value="Youth">Youth</option>
            </select>

            <select
              value={entry.positionPlayed}
              onChange={(e) => updateClubStats(index, 'positionPlayed', e.target.value)}
              required
            >
              <option value="">Select Position Played</option>
              <option value="Goalkeeper">Goalkeeper</option>
              <option value="Defender">Defender</option>
              <option value="Midfielder">Midfielder</option>
              <option value="Forward">Forward</option>
            </select>

            <input
              type="number"
              placeholder="Matches"
              value={entry.matches}
              onChange={(e) => updateClubStats(index, 'matches', e.target.value)}
            />
            <input
              type="number"
              placeholder="Goals"
              value={entry.goals}
              onChange={(e) => updateClubStats(index, 'goals', e.target.value)}
            />
            <input
              type="number"
              placeholder="Assists"
              value={entry.assists}
              onChange={(e) => updateClubStats(index, 'assists', e.target.value)}
            />
            <input
              type="number"
              placeholder="Clean Sheets"
              value={entry.cleanSheets}
              onChange={(e) => updateClubStats(index, 'cleanSheets', e.target.value)}
            />
            <input
              type="text"
              placeholder="Club Website"
              value={entry.clubWebsite}
              onChange={(e) => updateClubStats(index, 'clubWebsite', e.target.value)}
            />

            <label>Upload Photo in This Club</label>
            <input
              type="file"
              accept="image/*"
              onChange={(e) => updateClubPhoto(index, e.target.files[0])}
            />
          </div>
        ))}

        <div className="form-button-group">
          <button type="button" onClick={addClubRow}>
            + Add Another Club
          </button>
        </div>

        <div className="submit-button-container">
          <button type="submit" className="submit-button">
            Submit Application
          </button>
        </div>
      </form>
    </div>
  );
};

export default ApplyVSNIndexForm;




